// SVG icon constant to avoid repetition
const CART_ICON_SVG = `<svg class="cart-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
  <circle cx="9" cy="21" r="1"></circle>
  <circle cx="20" cy="21" r="1"></circle>
  <path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6"></path>
</svg>`;

// Global cart state management
const CartState = {
  cache: null,
  listeners: new Set(),
  
  async fetch(force = false) {
    if (this.cache && !force) return this.cache;
    try {
      const res = await fetch("/cart.js");
      if (!res.ok) throw new Error(`HTTP error! status: ${res.status}`);
      this.cache = await res.json();
      return this.cache;
    } catch (err) {
      console.error('Error fetching cart:', err);
      return { items: [], item_count: 0, total_price: 0 };
    }
  },
  
  async update(force = true) {
    const cart = await this.fetch(force);
    this.notify(cart);
    return cart;
  },
  
  notify(cart) {
    this.listeners.forEach(callback => callback(cart));
  },
  
  subscribe(callback) {
    this.listeners.add(callback);
    return () => this.listeners.delete(callback);
  }
};

async function initCartForms(scope = document) {
  const productForms = scope.querySelectorAll(".new-product-form");

  async function getCart(force = false) {
    return CartState.fetch(force);
  }

  function showCartToast(message = "Added to Cart!") {
    let toast = document.querySelector(".cart-toast");
    if (!toast) {
      toast = document.createElement("div");
      toast.className = "cart-toast";
      document.body.appendChild(toast);
    }

    toast.textContent = message;

    // retrigger animation
    toast.classList.remove("show");
    void toast.offsetWidth; // force reflow
    toast.classList.add("show");

    setTimeout(() => {
      toast.classList.remove("show");

      // remove from DOM after transition ends
      setTimeout(() => {
        if (toast) toast.remove();
      }, 300);
    }, 2500);
  }

  function updateCartBubble(count) {
    const bubble = document.querySelector(".header__icon--cart .cart-count-bubble");
    if (bubble) {
      bubble.textContent = count;
    } else {
      const cartIcon = document.querySelector(".header__icon--cart");
      if (cartIcon) {
        const newBubble = document.createElement("div");
        newBubble.className = "cart-count-bubble flo-cart-icon-bubble";
        newBubble.textContent = count;
        cartIcon.appendChild(newBubble);
      }
    }
  }
  
  function notifyCartUpdate(cart) {
    // Notify Shopflo
    if (window.Shopflo && typeof window.Shopflo.refreshCart === 'function') {
      window.Shopflo.refreshCart();
    }
    
    // Trigger custom event for third-party apps
    document.dispatchEvent(new CustomEvent('cart:updated', { 
      detail: { cart: cart } 
    }));
    
    // Update global cart state
    CartState.cache = cart;
    CartState.notify(cart);
  }

  function debounce(fn, delay) {
    let timer;
    return (...args) => {
      clearTimeout(timer);
      timer = setTimeout(() => fn(...args), delay);
    };
  }

  // -------- CUSTOM QUANTITY (card only) --------
  function showQuantityInput(container, variantId, addBtn, quantity = 1) {
    const oldQty = container.querySelector(".card-quantity");
    if (oldQty) oldQty.remove();

    const qtyEl = document.createElement("div");
    qtyEl.className = "card-quantity";
    qtyEl.innerHTML = `
        <button class="quantity__button" name="minus" type="button">-</button>
        <input class="quantity__input" type="number" min="0" value="${quantity}">
        <button class="quantity__button" name="plus" type="button">+</button>
      `;

    addBtn.insertAdjacentElement("afterend", qtyEl);

    const minusBtn = qtyEl.querySelector('[name="minus"]');
    const plusBtn = qtyEl.querySelector('[name="plus"]');
    const input = qtyEl.querySelector(".quantity__input");

    async function updateCartQuantity(newQty) {
      if (newQty < 0) newQty = 0;
      try {
        const res = await fetch("/cart/update.js", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
          },
          body: JSON.stringify({ updates: { [variantId]: newQty } }),
        });
        const cart = await res.json();
        input.value = newQty;
        updateCartBubble(cart.item_count);
        notifyCartUpdate(cart);

        if (newQty <= 0) {
          qtyEl.remove();
          if (addBtn) addBtn.style.display = "flex";
        }
      } catch (err) {
        console.error("Cart update error:", err);
      }
    }

    minusBtn.addEventListener("click", () =>
      updateCartQuantity(parseInt(input.value) - 1)
    );
    plusBtn.addEventListener("click", () =>
      updateCartQuantity(parseInt(input.value) + 1)
    );
    input.addEventListener(
      "change",
      debounce(() => updateCartQuantity(parseInt(input.value) || 0), 300)
    );
  }

  // -------- BIND CUSTOM FORMS --------
  productForms.forEach((form) => {
    if (form.dataset.initialized) {
      return;
    }
    form.dataset.initialized = "true";

    const variants = JSON.parse(form.dataset.variants);
    const hiddenInput = form.querySelector("input[name='id']");
    const addBtn = form.querySelector(".atc-btn");
    const variantTabs = form.querySelectorAll(".variant-tab");
    const sectionId = form.dataset.sectionId;
    const productId = form.dataset.productId;
    
    let productContainer = form.querySelector(`#CardInfo-${sectionId}-${productId}`);
    if (!productContainer) {
      productContainer = form.parentElement?.querySelector(`#CardInfo-${sectionId}-${productId}`);
    }
    if (!productContainer) {
      productContainer = document.querySelector(`#CardInfo-${sectionId}-${productId}`);
    }
    
    const regularPriceEl = productContainer?.querySelector(".product-price .price-text");
    const salePriceEl = productContainer?.querySelector(".compare-product-price .price-text");

    let selectedOptions = [];

    function updateVariant(variant) {
      if (variant) {
        hiddenInput.value = variant.id;
        addBtn.disabled = !variant.available;
        addBtn.innerHTML = variant.available ? `${CART_ICON_SVG} Add to cart` : "Sold out";
        if (regularPriceEl) {
          regularPriceEl.textContent = `${variant.price / 100}`;
        }
        if (salePriceEl && variant.compare_at_price && variant.compare_at_price > variant.price) {
          salePriceEl.textContent = `${variant.compare_at_price / 100}`;
        }
      }
    }

    // Init variant
    const currentVariant = variants.find((v) => v.id == hiddenInput.value);
    if (currentVariant) {
      selectedOptions = [...currentVariant.options];
      currentVariant.options.forEach((opt, i) => {
        form
          .querySelector(
            `.variant-tab[data-option-index="${i + 1}"][data-value="${opt}"]`
          )
          ?.classList.add("active");
      });
      updateVariant(currentVariant);
    }

    // Variant switch
    variantTabs.forEach((tab) => {
      tab.addEventListener("click", () => {
        if (tab.classList.contains("disabled")) return;
        const optionIndex = parseInt(tab.dataset.optionIndex) - 1;
        selectedOptions[optionIndex] = tab.dataset.value;

        tab.parentElement.querySelectorAll(".variant-tab").forEach((sib) => sib.classList.remove("active"));
        tab.classList.add("active");

        const matchedVariant = variants.find((v) =>
          v.options.every((opt, i) => selectedOptions[i] === opt)
        );
        updateVariant(matchedVariant);

        if (matchedVariant) {
          syncFormWithCart(matchedVariant.id);
        }
      });
    });

    // Sync form UI with cart state
    function syncFormWithCart(variantId) {
      const cart = CartState.cache;
      if (!cart) return;
      
      const lineItem = cart.items.find(item => item.variant_id == variantId);
      
      if (lineItem) {
        addBtn.style.display = "none";
        const existingQty = form.querySelector(".card-quantity");
        if (existingQty) {
          const input = existingQty.querySelector(".quantity__input");
          if (input && parseInt(input.value) !== lineItem.quantity) {
            input.value = lineItem.quantity;
          }
        } else {
          showQuantityInput(form, lineItem.variant_id, addBtn, lineItem.quantity);
        }
      } else {
        const oldQty = form.querySelector(".card-quantity");
        if (oldQty) oldQty.remove();
        addBtn.style.display = "flex";
      }
    }

    // Add to cart
    addBtn.addEventListener("click", async () => {
      const variantId = hiddenInput.value;
      if (!variantId) return alert("Please select a variant first");

      const selectedVariant = variants.find(v => v.id == variantId);
      if (!selectedVariant || !selectedVariant.available) {
        return alert("Please select an available variant");
      }

      addBtn.disabled = true;
      addBtn.textContent = "Adding...";

      try {
        const res = await fetch("/cart/add.js", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
          },
          body: JSON.stringify({ id: variantId, quantity: 1 }),
        });
        const addedItem = await res.json();

        addBtn.disabled = false;
        addBtn.innerHTML = `${CART_ICON_SVG} Add to cart`;
        addBtn.style.display = "none";
        showQuantityInput(form, addedItem.variant_id, addBtn, addedItem.quantity);
        showCartToast("Added to Cart!");
        
        const cart = await CartState.update(true);
        updateCartBubble(cart.item_count);
      } catch (err) {
        console.error(err);
        addBtn.disabled = false;
        addBtn.innerHTML = `${CART_ICON_SVG} Add to cart`;
      }
    });

    // On load: check if already in cart
    getCart().then((cart) => {
      const lineItem = cart.items.find(item => item.variant_id == hiddenInput.value);
      if (lineItem) {
        addBtn.style.display = "none";
        showQuantityInput(form, lineItem.variant_id, addBtn, lineItem.quantity);
      }
      updateCartBubble(cart.item_count);
    });

    // Subscribe to cart updates (event-driven)
    const unsubscribe = CartState.subscribe((cart) => {
      const currentVariantId = parseInt(hiddenInput.value);
      syncFormWithCart(currentVariantId);
      updateCartBubble(cart.item_count);
    });

    // Cleanup subscription when form is removed
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        mutation.removedNodes.forEach((node) => {
          if (node === form || node.contains(form)) {
            unsubscribe();
            observer.disconnect();
          }
        });
      });
    });
    
    if (form.parentElement) {
      observer.observe(form.parentElement, { childList: true, subtree: true });
    }
  });
}

document.addEventListener("DOMContentLoaded", () => {
  // Initialize normal product forms immediately
  initCartForms();

  // Use MutationObserver to watch for related products (event-driven)
  const observer = new MutationObserver((mutations) => {
    mutations.forEach((mutation) => {
      mutation.addedNodes.forEach((node) => {
        if (node.nodeType === 1) {
          // Check if the added node is or contains related products grid
          const relatedGrid = node.classList?.contains('related-products') 
            ? node.querySelector('.grid')
            : node.querySelector?.('.related-products .grid');
          
          if (relatedGrid) {
            initCartForms(relatedGrid);
          }
        }
      });
    });
  });

  // Observe the document body for related products being added
  observer.observe(document.body, { 
    childList: true, 
    subtree: true 
  });

  // Check if related products already exist
  const existingRelatedGrid = document.querySelector(".related-products .grid");
  if (existingRelatedGrid) {
    initCartForms(existingRelatedGrid);
  }

  // Handle Shopify section reloads
  document.addEventListener("shopify:section:load", (event) => {
    const section = event.target;
    const relatedGrid = section.querySelector(".related-products .grid");
    if (relatedGrid) {
      initCartForms(relatedGrid);
    }
  });

  // Listen for external cart updates (from cart drawer, other scripts, etc.)
  document.addEventListener('cart:refresh', async () => {
    await CartState.update(true);
  });
  
  // Listen for Shopify theme editor events
  document.addEventListener('shopify:block:select', async () => {
    await CartState.update(true);
  });
});



